import { useEffect, useRef, useState } from 'react';
import type { ProductAnalysisProgressWsMessage } from '@ozon-ai/analysis/contracts';

function buildProductAnalysisWsUrl(runId: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/ws/product-analyses/${encodeURIComponent(runId)}`;
}

function parseWsMessage(data: unknown): ProductAnalysisProgressWsMessage | null {
  if (typeof data !== 'string') return null;
  try {
    return JSON.parse(data) as ProductAnalysisProgressWsMessage;
  } catch {
    return null;
  }
}

const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_MS = 1500;

export function useProductAnalysisProgressWs(
  runId: string | undefined,
  enabled: boolean,
  onMessage: (message: ProductAnalysisProgressWsMessage) => void
): { ready: boolean } {
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled || !runId) {
      setReady(false);
      return;
    }

    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempts = 0;
    let stopped = false;

    const connect = () => {
      if (stopped) return;

      ws = new WebSocket(buildProductAnalysisWsUrl(runId));

      ws.onopen = () => {
        reconnectAttempts = 0;
        setReady(true);
      };

      ws.onmessage = (event) => {
        const parsed = parseWsMessage(event.data);
        if (!parsed || parsed.type === 'connected') return;
        onMessageRef.current(parsed);
      };

      ws.onclose = () => {
        setReady(false);
        ws = null;
        if (stopped || reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return;
        reconnectAttempts += 1;
        reconnectTimer = setTimeout(connect, RECONNECT_MS);
      };

      ws.onerror = () => {
        ws?.close();
      };
    };

    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      ws?.close();
      setReady(false);
    };
  }, [enabled, runId]);

  return { ready };
}
